import { Avatar, Box, Button, Center, Column, Row, Typography } from '@tempmee/react';
import { CenteredSpinner } from '~/commonComponents';
import moment from 'moment';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { SHOW_MESSAGE } from '~/actions';
import Portal from '~/commonComponents/Portal';
import { useInfiniteEligibleJobs } from '~/domain/EligibleJobs';
import {
  IInviteProfessionalsToJobsPayload,
  jobKeys,
  useInviteProfessionalsToJobsMutation,
} from '~/domain/Jobs';
import { ProfessionTypeEnum } from '~/models';
import { routes } from '~/routes';
import { EligibleJobCard } from './EligibleJobCard';
import { OutlineXIcon } from '@tempmee/react-icons';

export const ConfirmCandidateEligibleJobsPopup = ({
  show,
  candidate,
  professionalId,
  officeId,
  job,
  onClose: closePopup,
  navigateWithoutBlocking,
}: {
  show: boolean;
  candidate: {
    id?: string;
    full_name: string;
    profile_image?: string | null;
  } | null;
  professionalId: string | null;
  officeId: string | null;
  job: {
    local_date?: string;
    profession?: string;
  } | null;
  onClose: () => void;
  navigateWithoutBlocking: (path: string) => void;
}) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const inviteProfessionalsToJobsMutation = useInviteProfessionalsToJobsMutation();
  const [selectedJobIds, setSelectedJobIds] = useState<string[]>([]);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isInviting = inviteProfessionalsToJobsMutation.isPending;

  const { eligibleJobs, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useInfiniteEligibleJobs({
      xOfficeId: officeId ?? '',
      professionalId: professionalId ?? '',
      limit: 10,
      enabled: show && Boolean(professionalId) && Boolean(officeId),
      profession: job?.profession as ProfessionTypeEnum,
    });

  const handleJobSelect = useCallback((jobId: string) => {
    setSelectedJobIds((prev) => {
      if (prev.includes(jobId)) {
        return prev.filter((id) => id !== jobId);
      }
      return [...prev, jobId];
    });
  }, []);

  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const scrolledToBottom = scrollTop + clientHeight >= scrollHeight - 50;

    if (scrolledToBottom && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  const handlePopupClose = useCallback(
    (route: string) => {
      closePopup();
      setSelectedJobIds([]);
      navigateWithoutBlocking(route);
    },
    [closePopup, navigateWithoutBlocking],
  );

  const handleCloseClick = () => {
    handlePopupClose('/dashboard/jobs/scheduled');
  };

  const handleInviteSuccess = useCallback(
    (_data: unknown, payload: IInviteProfessionalsToJobsPayload) => {
      payload.jobIds.forEach((jobId) => {
        queryClient.invalidateQueries({ queryKey: jobKeys.detail(jobId) });
      });
      handlePopupClose('/');
      dispatch({
        type: SHOW_MESSAGE,
        payload: {
          isSuccess: true,
          popupProps: {
            rightButtonText: 'Close',
          },
          title: 'Request(s) Successfully Sent!',
          message: (
            <>
              <b>{candidate?.full_name}</b> has been invited to the selected shift(s). Once they
              accept at the listed rate, {candidate?.full_name.split(' ')[0]} will be confirmed!
            </>
          ),
        },
      });
    },
    [candidate, dispatch, handlePopupClose, queryClient],
  );

  const handleInviteClick = () => {
    if (selectedJobIds.length > 0) {
      inviteProfessionalsToJobsMutation.mutate(
        {
          professionalIds: [professionalId as string],
          jobIds: selectedJobIds,
          officeId: officeId as string,
        },
        {
          onSuccess: handleInviteSuccess,
        },
      );
    }
  };

  const onCardPress = (jobId: string) => {
    navigateWithoutBlocking(routes.jobDetails.replace(':jobId', jobId));
  };

  if (!show || !candidate || !job) {
    return null;
  }

  return (
    <Portal>
      <Column
        className="modal"
        style={{
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 9999,
        }}
      >
        <Column
          className="modal_content"
          style={{
            position: 'relative',
            padding: '40px 30px',
            maxWidth: 700,
            maxHeight: '90vh',
          }}
          onClick={(event) => event.stopPropagation()}
        >
          <Box
            onClick={handleCloseClick}
            data-testid="close-popup"
            style={{ position: 'absolute', top: 20, right: 20, cursor: 'pointer' }}
          >
            <OutlineXIcon />
          </Box>

          <Column align="center" style={{ marginTop: 12, marginBottom: 20 }}>
            <Avatar size="xl" src={candidate?.profile_image || undefined} alt="profile_image" />
          </Column>

          <Typography as="span" variant="body1" bold>
            Candidate Confirmed for {moment(job?.local_date).format('ddd, MMM D, YYYY')}
          </Typography>

          <Typography as="span" style={{ marginTop: 16 }}>
            Would you like to invite{' '}
            <Typography as="span" bold>
              {candidate?.full_name}
            </Typography>{' '}
            for other shifts?
          </Typography>

          <Column
            ref={scrollContainerRef}
            style={{
              flex: 1,
              marginTop: 16,
              marginBottom: 24,
              overflowY: 'auto',
              overflowX: 'hidden',
              maxHeight: 400,
              scrollbarWidth: 'thin',
            }}
          >
            {isLoading ? (
              <Center p={10}>
                <CenteredSpinner />
              </Center>
            ) : (
              <Column gap={2} px={2} pt={1} pb={2}>
                {eligibleJobs.map((job) => (
                  <EligibleJobCard
                    key={job.id}
                    job={job}
                    isSelected={selectedJobIds.includes(job.id)}
                    onSelect={handleJobSelect}
                    disabled={isInviting}
                    onCardPress={onCardPress}
                  />
                ))}

                {isFetchingNextPage && (
                  <Center p={4}>
                    <CenteredSpinner />
                  </Center>
                )}
              </Column>
            )}
          </Column>

          <Row gap={4} justify="center" style={{ marginTop: 16 }}>
            <Button variant="secondary" size="md" onClick={handleCloseClick} disabled={isInviting}>
              No Thanks
            </Button>

            <Button
              onClick={handleInviteClick}
              disabled={selectedJobIds.length === 0}
              size="md"
              isLoading={isInviting}
            >
              {selectedJobIds.length > 0
                ? `Invite for ${selectedJobIds.length} Shift${selectedJobIds.length !== 1 ? 's' : ''}`
                : 'Invite'}
            </Button>
          </Row>
        </Column>
      </Column>
    </Portal>
  );
};
